import { useCallback, useState } from 'react';

import { useConfiguratorDispatch, useRootSelector } from '../../../store';
import { Material } from '../../../types';
import { ContainedButton } from '../../../components/buttons';

import { UpsellProduct } from '../types';
import { addUpsell } from '../store/upsellsSlice';
import {
  useAddMultiConfigUpsellToCart,
  useAddUpsellToCart
} from '../../cart/useAddToCart';
import {
  selectMultiConfigEnabled,
  selectSubmittedCartItemsByConfigId
} from '../../multiConfig';

interface AddUpsellButtonProps {
  upsell: UpsellProduct;
  selectedMaterial: Material;
  immediateAddToCart?: boolean;
  onUpsellAdded?: () => void;
  productTag?: string;
  baseProductCartItemId?: string;
}

export function AddUpsellButton({
  upsell,
  selectedMaterial,
  immediateAddToCart = false,
  onUpsellAdded,
  productTag,
  baseProductCartItemId
}: AddUpsellButtonProps) {
  const dispatch = useConfiguratorDispatch();
  const [buttonText, setButtonText] = useState('Add to Cart');
  const [isLoading, setIsLoading] = useState(false);

  const multiConfigEnabled = useRootSelector(selectMultiConfigEnabled);
  const submittedCartItemsByConfigId = useRootSelector(
    selectSubmittedCartItemsByConfigId
  );
  const isMultiConfigSession =
    multiConfigEnabled && Object.keys(submittedCartItemsByConfigId).length > 0;

  const addUpsellToCart = useAddUpsellToCart(
    upsell,
    selectedMaterial,
    baseProductCartItemId,
    productTag
  );
  const addUpsellToAllConfigs = useAddMultiConfigUpsellToCart(
    upsell,
    selectedMaterial,
    productTag
  );

  const handleImmediateAddToCart = useCallback(async () => {
    setIsLoading(true);
    setButtonText('Adding...');

    const result = isMultiConfigSession
      ? await addUpsellToAllConfigs()
      : await addUpsellToCart();

    if (result.success) {
      if (onUpsellAdded) {
        onUpsellAdded();
      }
    } else {
      setButtonText('Error Adding To Cart');
      setIsLoading(false);
    }
  }, [
    isMultiConfigSession,
    addUpsellToAllConfigs,
    addUpsellToCart,
    onUpsellAdded
  ]);

  const handleClick = useCallback(() => {
    // For a multi-config session, always add immediately (per-configuration)
    // regardless of the immediateAddToCart prop - that prop reflects the
    // single-item convention (defer until an explicit "Save"), but there's
    // no single-item "primary" save step here to defer to while editing an
    // existing session (the deferred queue is never read by the
    // multi-config save path), so deferring would silently drop the choice.
    if (immediateAddToCart || isMultiConfigSession) {
      handleImmediateAddToCart();
    } else {
      dispatch(
        addUpsell({
          upsell,
          material: selectedMaterial
        })
      );
    }
  }, [
    immediateAddToCart,
    isMultiConfigSession,
    handleImmediateAddToCart,
    dispatch,
    upsell,
    selectedMaterial
  ]);

  return (
    <ContainedButton onClick={handleClick} disabled={isLoading}>
      {buttonText}
    </ContainedButton>
  );
}
